// eslint-disable-next-line no-restricted-imports
import {PathParam, createSearchParams, generatePath} from 'react-router-dom';

import {
  defaultTo,
  identity,
  ifElse,
  mapObjIndexed,
  mergeRight,
  pipe,
  reject,
  toPairs,
  tryCatch,
  isNotEmpty,
} from 'ramda';
import {isNotString, isString} from 'ramda-adjunct';

import {Nullish} from '../types/Nullish';

// Distributes over path unions — react-router v7's PathParam collapses `keyof` of a union to the
// key intersection, which would drop params existing on only some union members.
export type ExtractParams<Path extends string> = Path extends unknown
  ? {[key in PathParam<Path>]: string | Nullish}
  : never;

type BaseOptions = {
  queryParams?: Record<string, string | Nullish> | string;
  isSearchParamsPreserved?: boolean;
};

// Distributive for the same reason as ExtractParams — checked per union member, so a mixed union
// ('/a' | '/b/:id') requires params matching the member instead of accepting {} for all of them.
export type OptionsType<Path extends string> = Path extends unknown
  ? ExtractParams<Path> extends Record<string, never>
    ? BaseOptions
    : BaseOptions & {params: ExtractParams<Path>}
  : never;

/**
 * @about Composes a strictly typed URL path based on the provided route and options.
 * @params path: string, options: {params, queryParams}, isSearchParamsPreserved: boolean
 * @param mergeSourceQueryParams When passed (e.g. from `useComposePath` in shared/web), used instead of
 * `window.location.search` as the "current" query for merging — avoids stale links when the router
 * is ahead of the browser URL.
 * @returns {string} The composed URL path.
 * @example composePath("/users/:userId", {params: {userId: "123"}, queryParams: {query: "param"}})
 * // returns "/users/123?query=param"
 */
export const composePath = <Path extends string>(
  path: Path,
  options?: OptionsType<Path>,
  mergeSourceQueryParams?: Record<string, string>
): string => {
  const params = isWithParams(options) ? mapObjIndexed(defaultTo(null), options.params) : undefined;
  const isSearchParamsPreserved = options?.isSearchParamsPreserved ?? true;
  // Widened to `string` — v7 requires `string` for required params, while composePath's contract
  // allows Nullish values (mapped to null above); strictness is enforced by OptionsType instead.
  const url = generatePath<string>(path, params);
  const currentSearchParamsRecord =
    mergeSourceQueryParams ??
    Object.fromEntries(new URL(window.location.href).searchParams.entries());

  // Convert the string query params to a record
  const queryParams = getQueryParams(options?.queryParams);
  // Overwrite the duplicate current search params with the new ones
  const mergedSearchParams = mergeRight(currentSearchParamsRecord, queryParams);
  const newSearchParams = isSearchParamsPreserved ? mergedSearchParams : queryParams;
  const validSearchParams = getValidSearchParams(newSearchParams);
  const urlSearchParams = createSearchParams(validSearchParams);
  const isSearchNotEmpty = isNotEmpty([...urlSearchParams.entries()]);
  const searchParamsString = isSearchNotEmpty ? `?${urlSearchParams.toString()}` : '';

  return `${url}${searchParamsString}`;
};

const isWithParams = <Path extends string>(
  val: Record<string, unknown> | undefined
): val is {params: ExtractParams<Path>} => 'params' in (val ?? {});

const getValidSearchParams = pipe(
  defaultTo({}),
  toPairs<Record<string, string>>,
  reject(([_, val]) => isNotString(val))
);

const getQueryParams: (a: BaseOptions['queryParams']) => Record<string, string> = pipe(
  defaultTo({}),
  ifElse(
    isString,
    tryCatch(
      pipe((a) => new URLSearchParams(a).entries(), Object.fromEntries),
      () => {}
    ),
    identity
  )
);
